import React from 'react';
/**
 * Hook para gerenciamento de auditores e locais de contagem
 *
 * Responsabilidades:
 * - Lista de auditores participantes
 * - Lista de locais selecionados
 * - Adicionar/Remover auditores
 * - Adicionar/Remover locais
 * - Pontos de contagem
 */
import { useState, useCallback } from 'react';
import type { User } from '../services/userService';
import type { LocalContagem } from '../types/estabelecimento';

export interface PontoContagem {
  ponto: string;
  responsavel: string;
}

export interface UseContagemAuditoresReturn {
  // Auditores
  auditoresParticipantes: User[];
  setAuditoresParticipantes: React.Dispatch<React.SetStateAction<User[]>>;
  selectedAuditorId: number;
  setSelectedAuditorId: React.Dispatch<React.SetStateAction<number>>;
  handleAddAuditor: (auditoresDisponiveis: User[], onModification?: () => void) => void;
  handleRemoveAuditor: (onModification?: () => void) => void;

  // Locais de Contagem
  locaisContagemSelecionados: LocalContagem[];
  setLocaisContagemSelecionados: React.Dispatch<React.SetStateAction<LocalContagem[]>>;
  selectedLocalId: number;
  setSelectedLocalId: React.Dispatch<React.SetStateAction<number>>;
  handleAddLocal: (locaisDisponiveis: LocalContagem[], onModification?: () => void) => void;
  handleRemoveLocal: (onModification?: () => void) => void;

  // Pontos de Contagem
  pontosContagem: PontoContagem[];
  setPontosContagem: React.Dispatch<React.SetStateAction<PontoContagem[]>>;
  addPontoContagem: () => void;
  removePontoContagem: (index: number) => void;
  updatePontoContagem: (index: number, field: keyof PontoContagem, value: string) => void;
}

export function useContagemAuditores(): UseContagemAuditoresReturn {
  // Auditores state
  const [auditoresParticipantes, setAuditoresParticipantes] = useState<User[]>([]);
  const [selectedAuditorId, setSelectedAuditorId] = useState<number>(0);

  // Locais de Contagem state
  const [locaisContagemSelecionados, setLocaisContagemSelecionados] = useState<LocalContagem[]>([]);
  const [selectedLocalId, setSelectedLocalId] = useState<number>(0);

  // Pontos de Contagem state
  const [pontosContagem, setPontosContagem] = useState<PontoContagem[]>([]);

  // Auditor handlers
  const handleAddAuditor = useCallback((
    auditoresDisponiveis: User[],
    onModification?: () => void
  ) => {
    if (!selectedAuditorId) return;

    const auditor = auditoresDisponiveis.find(a => a.id === selectedAuditorId);
    if (auditor && !auditoresParticipantes.some(p => p.id === auditor.id)) {
      const newAuditores = [...auditoresParticipantes, auditor];
      setAuditoresParticipantes(newAuditores);
      setSelectedAuditorId(0);
      onModification?.();
    }
  }, [selectedAuditorId, auditoresParticipantes]);

  const handleRemoveAuditor = useCallback((onModification?: () => void) => {
    if (!selectedAuditorId) return;

    const newAuditores = auditoresParticipantes.filter(a => a.id !== selectedAuditorId);
    setAuditoresParticipantes(newAuditores);
    setSelectedAuditorId(0);
    onModification?.();
  }, [selectedAuditorId, auditoresParticipantes]);

  // Local handlers
  const handleAddLocal = useCallback((
    locaisDisponiveis: LocalContagem[],
    onModification?: () => void
  ) => {
    if (!selectedLocalId) return;

    const local = locaisDisponiveis.find(l => l.id === selectedLocalId);
    if (local && !locaisContagemSelecionados.some(p => p.id === local.id)) {
      const newLocais = [...locaisContagemSelecionados, local];
      setLocaisContagemSelecionados(newLocais);
      setSelectedLocalId(0);
      onModification?.();
    }
  }, [selectedLocalId, locaisContagemSelecionados]);

  const handleRemoveLocal = useCallback((onModification?: () => void) => {
    if (!selectedLocalId) return;

    const newLocais = locaisContagemSelecionados.filter(l => l.id !== selectedLocalId);
    setLocaisContagemSelecionados(newLocais);
    setSelectedLocalId(0);
    onModification?.();
  }, [selectedLocalId, locaisContagemSelecionados]);

  // Ponto de contagem handlers
  const addPontoContagem = useCallback(() => {
    setPontosContagem(prev => [...prev, { ponto: '', responsavel: '' }]);
  }, []);

  const removePontoContagem = useCallback((index: number) => {
    setPontosContagem(prev => prev.filter((_, i) => i !== index));
  }, []);

  const updatePontoContagem = useCallback((
    index: number,
    field: keyof PontoContagem,
    value: string
  ) => {
    setPontosContagem(prev => {
      const updated = [...prev];
      updated[index][field] = value;
      return updated;
    });
  }, []);

  return {
    // Auditores
    auditoresParticipantes,
    setAuditoresParticipantes,
    selectedAuditorId,
    setSelectedAuditorId,
    handleAddAuditor,
    handleRemoveAuditor,

    // Locais de Contagem
    locaisContagemSelecionados,
    setLocaisContagemSelecionados,
    selectedLocalId,
    setSelectedLocalId,
    handleAddLocal,
    handleRemoveLocal,

    // Pontos de Contagem
    pontosContagem,
    setPontosContagem,
    addPontoContagem,
    removePontoContagem,
    updatePontoContagem,
  };
}
